import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import { DEFAULT_TAX_RATE, UNIT_LABELS, unitNights } from "@/lib/articleConstants";
import { todayLocal } from "@/lib/bookingDates";
import { formatCurrency, netAmountFromGross, parseAmount } from "@/lib/money";
import { ProductBarcodeLookupField } from "@/components/pos/ProductBarcodeLookupField";
import {
  accountSource,
  supplierSource,
} from "@/lib/autocompleteSources";
import { TaxRateSelect } from "@/pages/stammdaten/TaxRateSelect";
import { ArticleShopContentSection } from "@/pages/stammdaten/ArticleShopContentSection";
import type {
  Account,
  ArticleCategory,
  ProductLookupResult,
  SuggestedArticleNumber,
  Supplier,
} from "@/types/camping";

const EMPTY_FORM = {
  category_id: "",
  account_id: "",
  supplier_id: "",
  article_number: "",
  name: "",
  unit: "night",
  tax_rate_percent: DEFAULT_TAX_RATE,
  barcode: "",
  purchase_price: "",
  requires_pickup_receipt: false,
  for_guest_shop: false,
  shop_description: "",
  shop_image_url: null as string | null,
  initial_valid_from: todayLocal(),
  initial_valid_until: "",
  initial_amount: "",
};

export interface CreateArticleModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateArticleModal({ open, onClose, onCreated }: CreateArticleModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierOption, setSupplierOption] = useState<AutocompleteOption<Supplier> | null>(null);
  const [accountOption, setAccountOption] = useState<AutocompleteOption<Account> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const loadSupplierOptions = useCallback(
    (query: string) => supplierSource(suppliers, query),
    [suppliers],
  );
  const loadAccountOptions = useCallback(
    (query: string) => accountSource(accounts, query),
    [accounts],
  );

  const loadCategories = useCallback(() => {
    api
      .get<ArticleCategory[]>("/api/v1/article-categories")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const suggestNumber = useCallback(async (categoryId: string) => {
    if (!categoryId) return;
    setSuggesting(true);
    try {
      const result = await api.get<SuggestedArticleNumber>(
        `/api/v1/article-categories/${categoryId}/suggest-number`,
      );
      setForm((f) => ({ ...f, article_number: result.suggested_number }));
    } catch {
      /* Vorschlag optional – manuelle Eingabe bleibt möglich */
    } finally {
      setSuggesting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM, initial_valid_from: todayLocal() });
    setSupplierOption(null);
    setAccountOption(null);
    setError(null);
    setSubmitting(false);
    loadCategories();
    api
      .get<Account[]>("/api/v1/accounts")
      .then(setAccounts)
      .catch(() => setAccounts([]));
    api
      .get<Supplier[]>("/api/v1/suppliers?include_inactive=true")
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, [open, loadCategories]);

  useEffect(() => {
    if (!open || !form.category_id) return;
    void suggestNumber(form.category_id);
  }, [open, form.category_id, suggestNumber]);

  const taxRate = Number(form.tax_rate_percent) || 0;
  const previewGross = parseAmount(form.initial_amount);
  const previewNet =
    previewGross != null ? netAmountFromGross(previewGross, taxRate) : null;
  const purchaseNetPreview = netAmountFromGross(form.purchase_price, taxRate);

  const selectedCategory = categories.find((c) => c.id === form.category_id);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/articles", {
        category_id: form.category_id || null,
        account_id: form.account_id || null,
        supplier_id: form.supplier_id || null,
        article_number: form.article_number.trim(),
        name: form.name.trim(),
        unit: form.unit,
        tax_rate_percent: Number(form.tax_rate_percent),
        barcode: form.barcode.trim() || null,
        purchase_price: purchaseNetPreview,
        requires_pickup_receipt: form.requires_pickup_receipt,
        for_guest_shop: form.for_guest_shop,
        shop_description: form.for_guest_shop ? form.shop_description.trim() || null : null,
        shop_image_url: form.for_guest_shop ? form.shop_image_url : null,
        initial_valid_from: form.initial_valid_from,
        initial_valid_until: previewNet != null ? form.initial_valid_until || null : null,
        initial_sale_price: previewNet,
      });

      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neuer Artikel"
      description="Stammdaten und optional den ersten Brutto-Preis anlegen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
        <ProductBarcodeLookupField
          barcode={form.barcode}
          onBarcodeChange={(barcode) => setForm((f) => ({ ...f, barcode }))}
          onLookupResult={(result: ProductLookupResult) =>
            setForm((f) => ({
              ...f,
              barcode: result.barcode,
              name: result.name ?? f.name,
              tax_rate_percent: result.suggested_tax_rate_percent ?? f.tax_rate_percent,
            }))
          }
          disabled={submitting}
        />
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-label">Stammdaten</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Artikelkategorie
              <select
                className="eh-select-input"
                value={form.category_id}
                onChange={(e) => {
                  const category_id = e.target.value;
                  setForm((f) => ({ ...f, category_id, article_number: "" }));
                }}
              >
                <option value="">— keine Kategorie —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.article_number_pattern})
                  </option>
                ))}
              </select>
              {selectedCategory ? (
                <span className="text-xs text-label-secondary">
                  Schema: {selectedCategory.article_number_pattern}
                  {suggesting ? " · Nummer wird vorgeschlagen…" : null}
                </span>
              ) : null}
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Artikelnummer
              <div className="flex gap-2">
                <input
                  required
                  autoFocus={!form.category_id}
                  className="eh-input min-w-0 flex-1 font-mono"
                  value={form.article_number}
                  onChange={(e) => setForm((f) => ({ ...f, article_number: e.target.value }))}
                />
                {form.category_id ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="shrink-0"
                    loading={suggesting}
                    onClick={() => void suggestNumber(form.category_id)}
                  >
                    Vorschlagen
                  </Button>
                ) : null}
              </div>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Bezeichnung
              <input
                required
                className="eh-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Einheit
              <select
                className="eh-select-input"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              >
                {Object.entries(UNIT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {unitNights(form.unit) != null ? (
                <span className="text-xs text-label-secondary">
                  1 {UNIT_LABELS[form.unit]} = {unitNights(form.unit)} Nächte (Referenz für
                  Teilberechnungen)
                </span>
              ) : null}
            </label>
            <label className="flex flex-col gap-1 text-sm">
              EK brutto (optional)
              <input
                type="number"
                step="0.01"
                min="0"
                className="eh-input"
                value={form.purchase_price}
                onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))}
              />
              {purchaseNetPreview != null ? (
                <span className="text-xs text-label-secondary">
                  Netto (Buchhaltung):{" "}
                  <span className="font-medium tabular-nums text-label">
                    {formatCurrency(purchaseNetPreview)}
                  </span>
                </span>
              ) : null}
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.requires_pickup_receipt}
                onChange={(e) =>
                  setForm((f) => ({ ...f, requires_pickup_receipt: e.target.checked }))
                }
              />
              Abholbeleg nach Verkauf drucken
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.for_guest_shop}
                onChange={(e) =>
                  setForm((f) => ({ ...f, for_guest_shop: e.target.checked }))
                }
              />
              Im Gäste-Shop (Vorbestellung)
            </label>
            {form.for_guest_shop ? (
              <div className="sm:col-span-2">
                <ArticleShopContentSection
                  value={{
                    shop_description: form.shop_description,
                    shop_image_url: form.shop_image_url,
                  }}
                  onChange={(shopContent) =>
                    setForm((f) => ({
                      ...f,
                      shop_description: shopContent.shop_description,
                      shop_image_url: shopContent.shop_image_url,
                    }))
                  }
                  articleName={form.name || "Neuer Artikel"}
                  canEdit
                  onError={setError}
                />
              </div>
            ) : null}
            <LockedAutocomplete
              className="sm:col-span-2"
              label="Lieferant (optional)"
              placeholder="Lieferant suchen…"
              value={supplierOption}
              loadOptions={loadSupplierOptions}
              onChange={(option) => {
                setSupplierOption(option);
                setForm((f) => ({ ...f, supplier_id: option?.value.id ?? "" }));
              }}
            />
            <LockedAutocomplete
              className="sm:col-span-2"
              label="Erlöskonto (optional)"
              placeholder="Kontonummer oder Bezeichnung suchen…"
              value={accountOption}
              loadOptions={loadAccountOptions}
              onChange={(option) => {
                setAccountOption(option);
                setForm((f) => ({ ...f, account_id: option?.value.id ?? "" }));
              }}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-label">Mehrwertsteuer</legend>
          <TaxRateSelect
            value={form.tax_rate_percent}
            onChange={(tax_rate_percent) => setForm((f) => ({ ...f, tax_rate_percent }))}
          />
        </fieldset>

        <fieldset className="space-y-3 rounded-lg border border-separator bg-fill-tertiary/50 p-4">
          <legend className="px-1 text-sm font-semibold text-label">Erster Preis (optional)</legend>
          <p className="text-xs text-label-secondary">
            Brutto-Preis mit optionalem Ablaufdatum. Ohne Ablauf gilt er, bis ein späterer Preis beginnt.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              Gültig ab
              <input
                type="date"
                className="eh-input"
                value={form.initial_valid_from}
                onChange={(e) => setForm((f) => ({ ...f, initial_valid_from: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Gültig bis
              <input
                type="date"
                className="eh-input"
                value={form.initial_valid_until}
                onChange={(e) => setForm((f) => ({ ...f, initial_valid_until: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Brutto-Preis (VK)
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="eh-input w-full pr-8"
                  placeholder="0,00"
                  value={form.initial_amount}
                  onChange={(e) => setForm((f) => ({ ...f, initial_amount: e.target.value }))}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-label-secondary">
                  €
                </span>
              </div>
            </label>
          </div>
          {previewNet != null ? (
            <p className="text-xs text-label-secondary">
              Netto (Buchhaltung):{" "}
              <span className="font-medium tabular-nums text-label">
                {formatCurrency(previewNet)}
              </span>{" "}
              ({taxRate} % MwSt.)
            </p>
          ) : null}
        </fieldset>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Artikel anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
